import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

export type ClubSubscriptionType = 'free' | 'monthly' | 'one_time';
export type ClubNearestEventsType = 'now' | 'this_week' | 'empty';

export interface AdminClubOwner {
  id: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
}

export interface AdminClubListItem {
  id: string;
  name: string;
  imageUrl: string | null;
  subscriptionType: ClubSubscriptionType;
  subscriptionPrice: number | null;
  nearestEvents: ClubNearestEventsType;
  memberCount: number;
  createdAt: string;
  owner: AdminClubOwner | null;
}

export interface AdminClubListResponse {
  items: AdminClubListItem[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminClubDetail extends AdminClubListItem {
  description: string | null;
  videos: Array<{ id: string; name: string; url: string }> | null;
  updatedAt: string;
}

export interface AdminClubMember {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl: string | null;
  isOwner: boolean;
}

export interface ListClubsArgs {
  search?: string;
  page?: number;
  limit?: number;
}

export const clubsApi = createApi({
  reducerPath: 'clubsApi',
  baseQuery,
  tagTypes: ['Club', 'ClubList'],
  endpoints: (builder) => ({
    listClubs: builder.query<AdminClubListResponse, ListClubsArgs>({
      query: ({ search, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (search && search.trim()) params.set('search', search.trim());
        params.set('page', String(page));
        params.set('limit', String(limit));
        return { url: `/admin/clubs?${params.toString()}` };
      },
      providesTags: ['ClubList'],
    }),
    getClub: builder.query<AdminClubDetail, string>({
      query: (id) => ({ url: `/admin/clubs/${id}` }),
      providesTags: (_result, _error, id) => [{ type: 'Club', id }],
    }),
    getClubMembers: builder.query<AdminClubMember[], string>({
      query: (id) => ({ url: `/admin/clubs/${id}/members` }),
      providesTags: (_result, _error, id) => [
        { type: 'Club', id: `${id}-members` },
      ],
    }),
    removeClubMember: builder.mutation<
      { success: boolean },
      { clubId: string; userId: string }
    >({
      query: ({ clubId, userId }) => ({
        url: `/admin/clubs/${clubId}/members/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { clubId }) => [
        { type: 'Club', id: clubId },
        { type: 'Club', id: `${clubId}-members` },
        'ClubList',
      ],
    }),
  }),
});

export const {
  useListClubsQuery,
  useGetClubQuery,
  useGetClubMembersQuery,
  useRemoveClubMemberMutation,
} = clubsApi;
